// Weight & Maturity Qualification (wmq.*) + Gap Analysis (gap.*) page strings.
// DB enum/data values (resource type, capability nature/domain), brand nouns,
// and numeric range labels are intentionally NOT translated.

export const fr: Record<string, string> = {
  // ── Weight & Maturity Qualification ──
  'wmq.noResources': 'Aucune ressource',
  'wmq.resource': 'ressource',
  'wmq.resources': 'ressources',
  'wmq.resourceCount.one': '{n} ressource',
  'wmq.resourceCount.other': '{n} ressources',
  'wmq.resourcesQualified.one': '{n} ressource qualifiée',
  'wmq.resourcesQualified.other': '{n} ressources qualifiées',
  'wmq.chosen': '{n} sélectionnée·s',
  'wmq.qualified': 'Qualifié·e',
  'wmq.showDescription': 'Afficher la description',
  'wmq.noDescription': 'Aucune description disponible.',
  'wmq.tooltip.criticality': 'Criticité : {n}',
  'wmq.tooltip.maturity': 'Maturité : {n}',
  'wmq.threshold.label': 'Criticité ≥ {n}',
  'wmq.threshold.selected': '{n} sél.',
  'wmq.threshold.manualOverride': 'Ajustement manuel — déplacez le curseur pour réinitialiser',
  'wmq.threshold.viewOnly': 'Lecture seule — accès administrateur requis pour modifier',
  'wmq.resetSelections': 'Réinitialiser les sélections',
  'wmq.empty.title': 'Aucune ressource qualifiée',
  'wmq.empty.desc': 'Aucune ressource n’a été qualifiée à l’étape 2 pour cette capacité.',
  'wmq.axis.critical': 'Critique',
  'wmq.axis.nonCritical': 'Non critique',
  'wmq.saving': 'Enregistrement…',
  'wmq.saveAndClose': 'Enregistrer et fermer',
  'wmq.importCsv': 'Importer un CSV',
  'wmq.exportCsv': 'Exporter en CSV',
  'wmq.alert.saveFailed': 'Échec de l’enregistrement des sélections',
  'wmq.alert.csvEmpty': 'Le fichier CSV est vide ou ne contient aucune ligne de données.',
  'wmq.alert.importComplete': 'Importation terminée : {imported} sélections importées, {skipped} ignorées.',
  'wmq.alert.importFailed': 'Échec de l’importation : ',

  // ── Gap Analysis ──
  'gap.title': 'Analyse des écarts capacité–ressource',
  'gap.subtitle': 'Écarts stratégiques où des ressources à forte criticité présentent une faible maturité. Écart = Criticité × (1 − Maturité/5).',
  'gap.noDescription': 'Aucune description disponible.',
  'gap.error.load': 'Échec du chargement des données d’analyse des écarts',
  'gap.empty.title': 'Aucune donnée d’analyse des écarts pour l’instant',
  'gap.empty.desc': 'Complétez d’abord l’étape 5 (Qualification pondération et maturité). Sélectionnez des paires capacité–ressource pour afficher la carte thermique des écarts.',
  'gap.level.critical': 'Critique',
  'gap.level.high': 'Élevé',
  'gap.level.medium': 'Moyen',
  'gap.level.low': 'Faible',
  'gap.level.ok': 'OK',
  'gap.legend.lowest': '<0,20',
  'gap.filter.capabilities': 'Capacités',
  'gap.filter.resources': 'Ressources',
  'gap.filter.all': 'Tout',
  'gap.sort.label': 'Trier :',
  'gap.sort.frequency': 'Fréquence',
  'gap.sort.worstGap': 'Pire écart',
  'gap.sort.lowestMaturity': 'Maturité la plus faible',
  'gap.noMatch': 'Aucune paire correspondante. Ajustez les filtres pour afficher des données.',
  'gap.col.resource': 'Ressource',
  'gap.abbr.maturity': 'M',
  'gap.abbr.criticality': 'C',
  'gap.tooltip.avgMaturity': 'Maturité moyenne : {n}',
  'gap.tooltip.appearsIn': 'Apparaît dans {n} capacités',
  'gap.tooltip.cell': 'Écart : {gap} | Criticité : {crit} | Maturité : {mat}',
};

export const en: Record<string, string> = {
  // ── Weight & Maturity Qualification ──
  'wmq.noResources': 'No resources',
  'wmq.resource': 'resource',
  'wmq.resources': 'resources',
  'wmq.resourceCount.one': '{n} resource',
  'wmq.resourceCount.other': '{n} resources',
  'wmq.resourcesQualified.one': '{n} resource qualified',
  'wmq.resourcesQualified.other': '{n} resources qualified',
  'wmq.chosen': '{n} chosen',
  'wmq.qualified': 'Qualified',
  'wmq.showDescription': 'Show description',
  'wmq.noDescription': 'No description available.',
  'wmq.tooltip.criticality': 'Criticality: {n}',
  'wmq.tooltip.maturity': 'Maturity: {n}',
  'wmq.threshold.label': 'Criticality ≥ {n}',
  'wmq.threshold.selected': '{n} sel.',
  'wmq.threshold.manualOverride': 'Manual override — move slider to reset',
  'wmq.threshold.viewOnly': 'View only — admin access required to edit',
  'wmq.resetSelections': 'Reset selections',
  'wmq.empty.title': 'No qualified resources',
  'wmq.empty.desc': 'No resources were qualified from Stage 2 for this capability.',
  'wmq.axis.critical': 'Critical',
  'wmq.axis.nonCritical': 'Non-critical',
  'wmq.saving': 'Saving...',
  'wmq.saveAndClose': 'Save & Close',
  'wmq.importCsv': 'Import CSV',
  'wmq.exportCsv': 'Export CSV',
  'wmq.alert.saveFailed': 'Failed to save selections',
  'wmq.alert.csvEmpty': 'CSV file is empty or has no data rows.',
  'wmq.alert.importComplete': 'Import complete: {imported} selections imported, {skipped} skipped.',
  'wmq.alert.importFailed': 'Import failed: ',

  // ── Gap Analysis ──
  'gap.title': 'Capability–Resource Gap Analysis',
  'gap.subtitle': 'Strategic gaps where high-criticality resources have low maturity. Gap = Criticality × (1 − Maturity/5).',
  'gap.noDescription': 'No description available.',
  'gap.error.load': 'Failed to load gap analysis data',
  'gap.empty.title': 'No Gap Analysis Data Yet',
  'gap.empty.desc': 'Complete Stage 5 (Weight & Maturity Qualification) first. Select capability–resource pairs to see the gap analysis heatmap.',
  'gap.level.critical': 'Critical',
  'gap.level.high': 'High',
  'gap.level.medium': 'Medium',
  'gap.level.low': 'Low',
  'gap.level.ok': 'OK',
  'gap.legend.lowest': '<0.20',
  'gap.filter.capabilities': 'Capabilities',
  'gap.filter.resources': 'Resources',
  'gap.filter.all': 'All',
  'gap.sort.label': 'Sort:',
  'gap.sort.frequency': 'Frequency',
  'gap.sort.worstGap': 'Worst Gap',
  'gap.sort.lowestMaturity': 'Lowest Maturity',
  'gap.noMatch': 'No matching pairs. Adjust filters to see data.',
  'gap.col.resource': 'Resource',
  'gap.abbr.maturity': 'M',
  'gap.abbr.criticality': 'R',
  'gap.tooltip.avgMaturity': 'Avg maturity: {n}',
  'gap.tooltip.appearsIn': 'Appears in {n} capabilities',
  'gap.tooltip.cell': 'Gap: {gap} | Criticality: {crit} | Maturity: {mat}',
};
